import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Users, Briefcase, Calendar, X } from "lucide-react";
import JobApplicationClient from "../data/api/JobApplicationClient";
import type { JobApplication } from "../data/api/JobApplicationClient";
import Loader from "../../../shared/components/Loader";
import ApplicationCard from "../components/ApplicationCard";
import Timeline from "../../../shared/components/Timeline";
import AddApplicantModal from "../components/AddApplicantModal";
import { useProspectiveCandidates } from "../hooks/useProspectiveCandidates";
import "../JobApplications.css";

const DEFAULT_STAGES = ["Applied", "Shortlisted", "Interviewed", "Offered", "Accepted", "Declined", "Rejected"];

const STAGE_COLOR_CLASS: Record<string, string> = {
  Applied: "applied",
  Screening: "screening",
  Interview: "interview",
  Offer: "offer",
  Hired: "hired",
  Rejected: "rejected",
};

const getStageClass = (stage: string) => STAGE_COLOR_CLASS[stage] || "applied";

interface JobApplicationsTimelineProps {
  isEmbedded?: boolean;
}

const JobApplicationsTimelinePage: React.FC<JobApplicationsTimelineProps> = ({ isEmbedded = false }) => {
  const navigate = useNavigate();
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const [searchParams] = useSearchParams();
  const opportunityTitle = searchParams.get("title") || "Opportunity";

  const [stages, setStages] = useState<string[]>(DEFAULT_STAGES);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<JobApplication | null>(null);
  const [timelineEntries, setTimelineEntries] = useState<Array<{title: string; subtitle?: string; dateRange?: string; badge?: string; badgeAccent?: "green" | "amber" | "red" | "neutral"; bullets?: string[]}>>([]);

  // ── Define loadData FIRST so callbacks are never stale ──────
  const loadData = useCallback(async () => {
    if (!opportunityId) return;
    try {
      const oppData = await JobApplicationClient.getStages(opportunityId);
      if (oppData && oppData.length > 0) {
        setStages(oppData);
      }
      const data = await JobApplicationClient.getApplicants(opportunityId);
      setApplications(data.records || []);
      setError(null);
    } catch (err: any) {
      setError(err?.message || "Failed to load applicants.");
    }
  }, [opportunityId]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    loadData().finally(() => setLoading(false));
  }, [loadData]);

  const loadTimeline = useCallback(async (candidateId: string) => {
    if (!opportunityId || !candidateId) return;
    try {
      const res = await JobApplicationClient.getApplicationTimeline(opportunityId, candidateId);
      setTimelineEntries(res.entries || (Array.isArray(res) ? res : []));
    } catch (err: any) {
      setError(err?.message || "Failed to load timeline.");
    }
  }, [opportunityId]);

  const handleStageMove = useCallback(
    async (app: JobApplication, targetStage: string) => {
      if (app.stage === targetStage || !opportunityId || !app.candidate_id) return;
      
      if (targetStage === "Rejected" || targetStage === "Declined") {
        setSelectedApp(app);
        return;
      }

      // Simplified - would need actual implementation
      setApplications((prev) =>
        prev.map((a) => (a.application_id === app.application_id ? { ...a, stage: targetStage } : a))
      );
      await loadData();
      await loadTimeline(app.candidate_id!);
    },
    [opportunityId, loadData, loadTimeline]
  );

  const handleOpenResume = useCallback((url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  // Group applications by stage
  const grouped = useMemo(() => {
    const map: Record<string, JobApplication[]> = {};
    stages.forEach((s) => (map[s] = []));
    applications.forEach((app) => {
      const key = app.stage || "Applied";
      if (map[key]) map[key].push(app);
      else map[key] = [app];
    });
    return map;
  }, [applications, stages]);

  if (loading) return <Loader isLoading={true} message="Loading applicants..." />;

  return (
    <div className={`kan-page${!isEmbedded ? " kan-page--standalone" : ""}`}>
      <div className="kan-board">
        {opportunityId && (
          stages.map((stage, stageIndex) => {
            const cards = grouped[stage] || [];
            const isCollapsed = false;

            return (
              <div key={stage} className="kan-column">
                <div className="kan-column-header">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span className="kan-column-title">
                      <span className={`kan-stage-dot ${getStageClass(stage)}`} />
                      {stage}
                    </span>
                    <span className="kan-column-count">{cards.length}</span>
                  </div>
                  <div className="kan-column-menu-wrapper" style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                    <button
                      className="kan-column-menu-btn"
                      onClick={() => {}}
                      title="Collapse Status"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '4px', borderRadius: '4px' }}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
                <div
                  className={`kan-column-body drop-zone`}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) {} }}
                  onDrop={(e) => {}}
                >
                  {cards.map((app: JobApplication) => {
                    const appKey = app.application_id || app.candidate_id || `${stage}-${Math.random()}`;
                    return (
                      <ApplicationCard
                        key={appKey}
                        app={app}
                        columnStage={stage}
                        isProcessing={false}
                        stageIndex={stageIndex}
                        totalStages={stages.length}
                        isSelected={false}
                        onToggleSelect={() => {}}
                        onDragStart={() => {}}
                        onDragEnd={() => {}}
                        onOpenResume={handleOpenResume}
                        onReject={() => setSelectedApp(app)}
                        onMoveToStage={(a) => handleStageMove(a, "next")}
                        onClickMatchScore={() => loadTimeline(app.candidate_id || "")}
                      />
                    );
                  })}
                  {cards.length === 0 && (
                    <div className="kan-empty-col">
                      <Users size={14} /> No applicants
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}

        {!opportunityId && (
          <div className="kan-empty-col">
            <Briefcase size={48} /> No opportunity selected. <br/> <small>Select an opportunity to view job applications.</small>
          </div>
        )}
      </div>

      {selectedApp && (
        <>
          <div className="kan-modal-backdrop" onClick={() => setSelectedApp(null)} />
          <div className="kan-modal">
            <h2 className="kan-modal-title">
              {selectedApp?.candidate_name || "Application Timeline"}
              <X size={16} className="kan-modal-close" onClick={() => setSelectedApp(null)} />
            </h2>
            <Timeline entries={timelineEntries} emptyText="No timeline data available." />
            <p className="kan-modal-subtitle">
              Current Stage: <span className={`kan-stage-dot ${getStageClass(selectedApp?.stage || "")}`}>{selectedApp?.stage || "Applied"}</span>
            </p>
            <button className="kan-modal-btn" onClick={() => setSelectedApp(null)}>
              Close
            </button>
          </div>
        </>
      )}

      <AddApplicantModal open={false} title={opportunityTitle} onClose={() => {}} onSubmit={async () => {}} />
    </div>
  );
};

export default JobApplicationsTimelinePage;